import { useQuery } from "@tanstack/react-query";
import type { PassId, PassType, PassShape } from "../models/PassId";
import {
	type GetPassIdsParams,
	getPassIds,
	getPassTypes,
	getPassShapes,
} from "../repositories/PassIdsRepository";

// ============================================
// Query Keys
// ============================================

export const PASS_IDS_QUERY_KEYS = {
	all: ["passIds"] as const,
	list: (params?: GetPassIdsParams) => ["passIds", "list", params] as const,
	detail: (id: number | null) => ["passIds", "detail", id] as const,
	passTypes: ["passIds", "passTypes"] as const,
	passShapes: ["passIds", "passShapes"] as const,
};

// ============================================
// usePassIds Hook
// ============================================

interface UsePassIdsProps {
	standId?: number | null;
	passTypeId?: number | null;
	passShapeId?: number | null;
}

interface UsePassIdsResult {
	passIds: PassId[];
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
}

export const usePassIds = (props: UsePassIdsProps = {}): UsePassIdsResult => {
	const params: GetPassIdsParams = {
		standId: props.standId,
		passTypeId: props.passTypeId,
		passShapeId: props.passShapeId,
		onlyActives: false,
	};

	const query = useQuery({
		queryKey: PASS_IDS_QUERY_KEYS.list(params),
		queryFn: () => getPassIds(params),
	});

	return {
		passIds: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error as Error | null,
		refetch: query.refetch,
	};
};

// ============================================
// usePassTypesLookup Hook (for dropdown)
// ============================================

export const usePassTypesLookup = () => {
	return useQuery<PassType[], Error>({
		queryKey: PASS_IDS_QUERY_KEYS.passTypes,
		queryFn: getPassTypes,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

// ============================================
// usePassShapesLookup Hook (for dropdown)
// ============================================

export const usePassShapesLookup = () => {
	return useQuery<PassShape[], Error>({
		queryKey: PASS_IDS_QUERY_KEYS.passShapes,
		queryFn: getPassShapes,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};
